import {
    Card,
    LegalPageShell,
    Section,
} from '@/components/LegalPage';

const SECTIONS = [
    { id: 'eligibility', title: '1. Eligibility & Accounts' },
    { id: 'roles', title: '2. Business Accounts & Team Roles' },
    { id: 'use', title: '3. Acceptable Use' },
    { id: 'content', title: '4. Your Content & Data' },
    { id: 'ip', title: '5. Our Intellectual Property' },
    { id: 'thirdparty', title: '6. Third-Party Services' },
    { id: 'availability', title: '7. Availability & Changes' },
    { id: 'fees', title: '8. Fees' },
    { id: 'termination', title: '9. Suspension & Termination' },
    { id: 'disclaimer', title: '10. Disclaimers' },
    { id: 'liability', title: '11. Limitation of Liability' },
    { id: 'indemnity', title: '12. Indemnification' },
    { id: 'law', title: '13. Governing Law' },
    { id: 'changes', title: '14. Changes to These Terms' },
    { id: 'contact', title: '15. Contact Us' },
];

function ChancoTermsAndConditions() {
    return (
        <LegalPageShell
            brand="Chanco"
            pageTitle="Terms & Conditions"
            documentTitle="Terms & Conditions | Chanco"
            effectiveDate="July 29, 2026"
            lastUpdated="July 29, 2026"
            toc={SECTIONS}
            footerNote="© 2026 Code X Pvt Ltd. All rights reserved. This page is the terms and conditions for the Chanco application."
            lead={(
                <>
                    <p>
                        <span className="font-medium text-foreground">Chanco</span>
                        {' '}
                        is a mobile app from
                        {' '}
                        <span className="font-medium text-foreground">Code X Pvt Ltd</span>
                        {' '}
                        (&quot;Code X&quot;, &quot;we&quot;, or &quot;our&quot;) that helps
                        businesses track inventory, manage orders and quotations, and coordinate
                        their teams. These Terms &amp; Conditions (the &quot;Terms&quot;) are
                        the agreement between you and Code X for your use of the Chanco app and
                        its backend services (together, the &quot;Service&quot;).
                    </p>
                    <p>
                        By creating an account or using Chanco, you accept these Terms. If
                        you&apos;re using the Service for a business, you confirm you&apos;re
                        authorized to accept them on that business&apos;s behalf. If you
                        don&apos;t agree, please don&apos;t use the Service.
                    </p>
                </>
            )}
        >
            <Section id="eligibility" title="1. Eligibility & Accounts">
                <p>
                    You must be at least 18 years old, or the age of majority in your
                    jurisdiction, to use the Service. To use most features you&apos;ll need an
                    account, and you agree to provide accurate, current information and keep it
                    up to date.
                </p>
                <p>
                    You&apos;re responsible for keeping your login credentials secure and for all
                    activity that happens under your account. Notify us promptly if you suspect
                    any unauthorized use.
                </p>
            </Section>

            <Section id="roles" title="2. Business Accounts & Team Roles">
                <p>
                    Chanco is designed around a business account that can include multiple team
                    members with different roles and permissions.
                </p>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>
                        The person who creates a business account (the &quot;owner&quot;) is
                        responsible for that account, including the actions of members they
                        invite and the data stored in it
                    </li>
                    <li>
                        Owners and administrators can invite, manage, assign permissions to, and
                        remove team members
                    </li>
                    <li>
                        If you join a business account created by someone else, that owner
                        controls the business data and your level of access, and may add or
                        remove your access at any time
                    </li>
                </ul>
            </Section>

            <Section id="use" title="3. Acceptable Use">
                <p>You agree not to:</p>
                <ul className="list-disc space-y-1.5 pl-5">
                    <li>Use the Service for any unlawful, fraudulent, or harmful purpose</li>
                    <li>
                        Upload content you don&apos;t have the right to use, or that infringes
                        others&apos; rights
                    </li>
                    <li>Attempt to access accounts, data, or systems that aren&apos;t yours</li>
                    <li>
                        Interfere with, disrupt, overload, reverse-engineer, or probe the
                        Service or its security
                    </li>
                    <li>
                        Copy, resell, or provide the Service to third parties except as
                        expressly allowed
                    </li>
                    <li>
                        Use automated means to scrape or extract data beyond normal use of the
                        app
                    </li>
                </ul>
            </Section>

            <Section id="content" title="4. Your Content & Data">
                <p>
                    You keep all rights to the data and content you add to Chanco — your
                    products, inventory, orders, quotations, customers, images, and reports
                    (&quot;Your Content&quot;). You grant Code X a limited license to host,
                    process, and display Your Content only as needed to operate and improve the
                    Service.
                </p>
                <p>
                    You&apos;re responsible for the accuracy and legality of Your Content and for
                    maintaining your own backups where important. Our handling of personal
                    information is described in our
                    {' '}
                    <a href="/tipa/privacy-policy" className="text-primary underline-offset-4 hover:underline">
                        Privacy Policy
                    </a>
                    .
                </p>
            </Section>

            <Section id="ip" title="5. Our Intellectual Property">
                <p>
                    The Service, including the Chanco name, logo, software, design, and content
                    we provide, is owned by Code X and protected by intellectual-property laws.
                    We grant you a limited, non-exclusive, non-transferable, revocable license to
                    use the app for your internal business purposes, subject to these Terms. No
                    other rights are granted.
                </p>
            </Section>

            <Section id="thirdparty" title="6. Third-Party Services">
                <p>
                    The Service relies on third-party platforms such as Expo, Google, and Apple
                    for app delivery, updates, notifications, and infrastructure. Your use of the
                    app through the Apple App Store or Google Play is also subject to those
                    stores&apos; terms. We&apos;re not responsible for third-party services we
                    don&apos;t control.
                </p>
            </Section>

            <Section id="availability" title="7. Availability & Changes">
                <p>
                    We work to keep the Service available and reliable, but we don&apos;t
                    guarantee it will be uninterrupted or error-free. We may add, change, or
                    remove features, and may perform maintenance that temporarily limits access.
                    We&apos;ll try to give reasonable notice of significant changes where
                    practical.
                </p>
            </Section>

            <Section id="fees" title="8. Fees">
                <p>
                    Some features may be offered free of charge and others may require payment.
                    Where fees apply, the applicable pricing and payment terms will be presented
                    to you before you incur any charge. Any paid plans, billing cycles, and
                    refund conditions will be described at the point of purchase.
                </p>
            </Section>

            <Section id="termination" title="9. Suspension & Termination">
                <p>
                    You may stop using the Service and delete your account at any time. We may
                    suspend or terminate your access if you breach these Terms, if required by
                    law, or to protect the Service or other users.
                </p>
                <p>
                    On termination, your right to use the Service ends. Some data may be retained
                    or deleted as described in our
                    {' '}
                    <a href="/tipa/privacy-policy" className="text-primary underline-offset-4 hover:underline">
                        Privacy Policy
                    </a>
                    {' '}
                    and applicable law.
                </p>
            </Section>

            <Section id="disclaimer" title="10. Disclaimers">
                <p>
                    The Service is provided &quot;as is&quot; and &quot;as available,&quot;
                    without warranties of any kind, whether express or implied, including fitness
                    for a particular purpose, merchantability, and non-infringement. We
                    don&apos;t warrant that the Service will meet your requirements or that
                    inventory, reports, or other outputs will be error-free. You&apos;re
                    responsible for decisions you make based on the app.
                </p>
            </Section>

            <Section id="liability" title="11. Limitation of Liability">
                <p>
                    To the maximum extent permitted by law, Code X will not be liable for any
                    indirect, incidental, special, consequential, or punitive damages, or for
                    lost profits, revenue, data, or business, arising from your use of the
                    Service. Our total liability for any claim relating to the Service will not
                    exceed the amount you paid us for the Service in the 12 months before the
                    claim, or, if you paid nothing, a nominal amount.
                </p>
            </Section>

            <Section id="indemnity" title="12. Indemnification">
                <p>
                    You agree to indemnify and hold Code X harmless from claims, damages, and
                    expenses (including reasonable legal fees) arising from your use of the
                    Service, Your Content, or your breach of these Terms or of applicable law.
                </p>
            </Section>

            <Section id="law" title="13. Governing Law">
                <p>
                    These Terms are governed by the laws of Nepal, without regard to its
                    conflict-of-laws rules. Disputes arising from these Terms or the Service will
                    be subject to the courts located in Nepal, unless applicable law requires
                    otherwise.
                </p>
            </Section>

            <Section id="changes" title="14. Changes to These Terms">
                <p>
                    We may update these Terms from time to time. When we do, we&apos;ll revise
                    the &quot;Last updated&quot; date above and, where appropriate, notify you
                    within the Service. Your continued use of Chanco after changes take effect
                    means you accept the updated Terms.
                </p>
            </Section>

            <Section id="contact" title="15. Contact Us">
                <p>If you have questions about these Terms, contact us at:</p>
                <Card className="text-[0.95rem] text-muted-foreground">
                    <p className="font-medium text-foreground">Code X Pvt Ltd</p>
                    <p className="mt-1">
                        Email:
                        {' '}
                        <a href="mailto:codexpokhara@gmail.com" className="text-primary underline-offset-4 hover:underline">
                            codexpokhara@gmail.com
                        </a>
                    </p>
                </Card>
            </Section>
        </LegalPageShell>
    );
}

export default ChancoTermsAndConditions;
